import React from 'react';
import FeaturePageTemplate from '../components/FeaturePageTemplate';

const QualityChecksPage = () => {
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );

  const benefits = [
    "Comprehensive no-code data quality monitoring",
    "Flexible rule creation for any data quality scenario",
    "Reduced time to implement quality checks across your data landscape",
    "Proactive alerting before data issues impact downstream systems",
    "Customizable monitoring schedules to match your data flows"
  ];

  const details = [
    {
      title: "Comprehensive Quality Coverage",
      description: "Qupid provides a managed set of no-code quality checks that cover all the essential aspects of data quality monitoring.",
      items: [
        "Completeness checks to detect missing data and null values",
        "Freshness monitoring to ensure timely data updates",
        "Schema validation to detect structural changes",
        "Uniqueness verification to prevent duplicate records",
        "Validity checks to ensure data meets expected patterns and ranges",
        "Volume monitoring to detect unexpected changes in data size"
      ]
    },
    {
      title: "Custom Rule Creation",
      description: "Beyond standard checks, Qupid allows you to create custom SQL-based rules for business-specific quality requirements.",
      items: [
        "SQL-based custom rule builder with syntax highlighting and validation",
        "Parameterized rule templates for quick deployment",
        "Business rule libraries for common industry-specific checks",
        "Rule versioning and change management",
        "Test mode for validating rules before deployment"
      ]
    },
    {
      title: "Flexible Scheduling and Execution",
      description: "Configure when and how your quality checks run to match your data operations.",
      items: [
        "Flexible monitoring frequency from minutes to months",
        "Event-based triggering for checks based on data pipeline completions",
        "Low operational overhead with dedicated query engine",
        "Resource management to prevent impact on production systems",
        "Priority-based scheduling for critical quality checks"
      ]
    },
    {
      title: "Coming Soon: Federated Quality Checks",
      description: "In Q2 2025, Qupid will introduce federated quality checks to address advanced use cases across multiple data sources.",
      items: [
        "Cross-Source Validation to ensure consistency between systems",
        "Duplicate Detection across disconnected data stores",
        "Data Transformation Verification to validate ETL processes",
        "End-to-end data flow quality validation",
        "Multi-source aggregation and reconciliation checks"
      ]
    }
  ];

  const useCases = [
    {
      title: "Data Warehouse Quality Assurance",
      description: "Implement comprehensive quality checks across your data warehouse to ensure analytics and reporting are based on reliable data."
    },
    {
      title: "ETL Pipeline Validation",
      description: "Monitor data quality before and after ETL processes to catch transformation errors and ensure data integrity through the pipeline."
    },
    {
      title: "Regulatory Compliance Monitoring",
      description: "Implement quality rules that ensure your data meets regulatory requirements for industries like finance, healthcare, and more."
    },
    {
      title: "BI Dashboard Reliability",
      description: "Ensure the underlying data feeding dashboards and reports meets quality standards before business decisions are made."
    }
  ];

  return (
    <FeaturePageTemplate
      title="Smart Quality Checks"
      description="Implement comprehensive data quality monitoring across your organization with Qupid's managed no-code quality rules, covering everything from completeness and freshness to schema integrity and custom business rules."
      icon={icon}
      benefits={benefits}
      details={details}
      useCases={useCases}
      additionalContent={
        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4">Quality Monitoring Architecture</h3>
          <p className="mb-4">
            Qupid's quality monitoring engine is designed for scalability and minimal impact. All quality checks run on a dedicated query engine, separate from your production systems, ensuring monitoring doesn't impact performance.
          </p>
          <p>
            Our architecture includes intelligent caching, incremental validation, and distributed execution to ensure even complex quality rules can run efficiently across petabyte-scale data sources. Results are stored historically, allowing trend analysis and anomaly detection on your data quality metrics themselves.
          </p>
        </div>
      }
    />
  );
};

export default QualityChecksPage; 